import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Text, Loader, Center, Box, ActionIcon, Group, Progress, Stack, Button, Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconX, IconChevronLeft, IconChevronRight, IconTextSize, IconMinus, IconPlus, IconMicrophone } from '@tabler/icons-react';
import { useStory } from '@/features/story/hooks/useStory';
import { useState, useEffect, useRef, useCallback } from 'react';
import { DictionaryPopover } from '@/features/story/components/DictionaryPopover';
import { useStartSession } from '@/features/quiz/hooks/useQuiz';
import { useStartSession as useStartFluencySession } from '@/features/fluency/hooks/useFluencyApi';
import { useChildren } from '@/features/parent/hooks/useParent';
import { useAuthStore } from '@/features/auth/store/authStore';
import { getThemeConfig } from '@/config/themes';
import { usePlatform } from '@/hooks/usePlatform';

// A word — letters (Unicode) plus an optional inner apostrophe/hyphen —
// wrapped so we can catch a plain tap. Punctuation and whitespace stay as
// plain text nodes so the paragraph flows naturally.
const WORD_TOKEN_SPLIT = /(\s+)/;
const LOOKUPABLE_WORD = /^([A-Za-z][A-Za-z'-]*[A-Za-z]|[A-Za-z])/;

function renderPageWithTappableWords(
  text: string,
  onWordTap: (word: string, el: HTMLElement) => void,
): React.ReactNode {
  return text.split(WORD_TOKEN_SPLIT).map((chunk, chunkIdx) => {
    if (!chunk) return null;
    if (/^\s+$/.test(chunk)) return chunk;
    // Peel off leading/trailing punctuation so we only include the actual
    // word in the tap surface — but the user still sees the punctuation
    // rendered inline.
    const match = chunk.match(LOOKUPABLE_WORD);
    if (!match) return chunk;
    const word = match[0];
    const before = '';
    const after = chunk.slice(word.length);
    return (
      <span key={chunkIdx}>
        {before}
        <span
          onClick={(e) => onWordTap(word, e.currentTarget)}
          style={{ cursor: 'pointer' }}
        >
          {word}
        </span>
        {after}
      </span>
    );
  });
}

/**
 * Mobile Story Reader - Page-based with Swipe Navigation
 *
 * Book-like reading experience:
 * - Paginated content (no scrolling)
 * - Swipe left/right to turn pages
 * - Full-screen immersive reading
 * - Touch-optimized text selection for dictionary
 * - Progress indicator
 */
export function MobileStoryReader() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((state) => state.user);
  const { isIOS } = usePlatform();
  const isStudent = user?.roles?.includes('student');
  const isParent = user?.roles?.includes('parent');

  // When a parent opens the story with ?studentId=<kid>, the popover credits
  // lookups to that kid's vocab deck instead of the parent's own account.
  const readingWithStudentId = isParent ? searchParams.get('studentId') : null;

  const { data: story, isLoading } = useStory(id!);
  const startSession = useStartSession();
  const startFluencySession = useStartFluencySession();
  const { data: children } = useChildren({ enabled: !!isParent });
  const fluencyChildren = (children ?? []).filter((c) => c.fluency_enabled);
  const [sessionId, setSessionId] = useState<string>('');
  const [readingTime, setReadingTime] = useState(0);
  const [selectedWord, setSelectedWord] = useState<string>('');
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [previousPage, setPreviousPage] = useState<number | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('large');
  const [showFontMenu, setShowFontMenu] = useState(false);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const hasStartedSession = useRef(false);

  // Get assignment ID from navigation state
  const assignmentId = location.state?.assignmentId;

  // Font size mapping - 3 distinct levels
  const fontSizes = {
    small: '1rem',      // 16px - Smallest
    medium: '1.25rem',  // 20px - Medium
    large: '1.5rem',    // 24px - Large (default, kid-friendly)
  };

  // Minimum swipe distance (in px) to trigger page turn
  const minSwipeDistance = 50;

  // Reset state when story ID changes
  useEffect(() => {
    setSessionId('');
    setReadingTime(0);
    setCurrentPage(0);
    hasStartedSession.current = false;
  }, [id]);

  // Start session when story loads (students only)
  useEffect(() => {
    if (story && isStudent && !hasStartedSession.current && !sessionId) {
      hasStartedSession.current = true;
      startSession.mutate(story.id, {
        onSuccess: (session) => {
          setSessionId(session.session_id);
        },
        onError: () => {
          hasStartedSession.current = false;
        },
      });
    }
  }, [story, isStudent, startSession, sessionId]);

  // Reading timer (students only)
  useEffect(() => {
    if (!isStudent) return;

    const interval = setInterval(() => {
      setReadingTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isStudent]);

  // Paginate story content with paragraph preservation
  useEffect(() => {
    if (!story || !pageContainerRef.current) return;

    const paginateText = () => {
      const container = pageContainerRef.current;
      if (!container) return;

      const text = story.passage;
      // Split by double newlines (paragraphs) or single newlines
      const paragraphs = text.split(/\n\n+/).map(p => p.trim()).filter(p => p.length > 0);
      const tempPages: string[] = [];
      let currentPageText = '';

      // Create a temporary element to measure text
      const measureElement = document.createElement('div');
      measureElement.style.cssText = `
        position: absolute;
        visibility: hidden;
        font-family: 'Comic Neue', 'Comic Sans MS', cursive;
        font-size: ${fontSizes[fontSize]};
        line-height: 1.8;
        white-space: pre-wrap;
        width: ${container.clientWidth - 48}px;
        padding: 24px;
      `;
      document.body.appendChild(measureElement);

      const maxHeight = container.clientHeight - 48; // Account for padding
      const targetFillPercentage = 0.85; // Try to fill at least 85% of page height

      for (let i = 0; i < paragraphs.length; i++) {
        const paragraph = paragraphs[i];
        const testText = currentPageText + (currentPageText ? '\n\n' : '') + paragraph;
        measureElement.textContent = testText;

        if (measureElement.clientHeight > maxHeight && currentPageText) {
          // Check if current page is reasonably filled
          measureElement.textContent = currentPageText;
          const currentHeight = measureElement.clientHeight;
          const fillPercentage = currentHeight / maxHeight;

          // If page is less than 85% full, try to split the current paragraph
          if (fillPercentage < targetFillPercentage && paragraph.length > 50) {
            // Try to split paragraph by sentences to fill more of the page
            const sentences = paragraph.split(/(?<=[.!?])\s+/);
            let partialParagraph = currentPageText;

            for (const sentence of sentences) {
              const testWithSentence = partialParagraph + (partialParagraph && !partialParagraph.endsWith('\n\n') ? ' ' : '') + sentence;
              measureElement.textContent = testWithSentence;

              if (measureElement.clientHeight > maxHeight) {
                // This sentence would overflow, save current page
                if (partialParagraph && partialParagraph !== currentPageText) {
                  tempPages.push(partialParagraph);
                  partialParagraph = sentence;
                } else {
                  // Current page was original content, save it
                  tempPages.push(currentPageText);
                  partialParagraph = paragraph;
                  break;
                }
              } else {
                partialParagraph = testWithSentence;
              }
            }
            currentPageText = partialParagraph;
          } else {
            // Page is reasonably full, save it and start new page
            tempPages.push(currentPageText);
            currentPageText = paragraph;
          }

          // Check if the new paragraph itself is too long
          measureElement.textContent = currentPageText;
          if (measureElement.clientHeight > maxHeight) {
            const sentences = currentPageText.split(/(?<=[.!?])\s+/);
            currentPageText = '';

            for (const sentence of sentences) {
              const testSentence = currentPageText + (currentPageText ? ' ' : '') + sentence;
              measureElement.textContent = testSentence;

              if (measureElement.clientHeight > maxHeight && currentPageText) {
                tempPages.push(currentPageText);
                currentPageText = sentence;
              } else {
                currentPageText = testSentence;
              }
            }
          }
        } else {
          currentPageText = testText;
        }
      }

      // Add the last page
      if (currentPageText.trim()) {
        tempPages.push(currentPageText);
      }

      document.body.removeChild(measureElement);
      setPages(tempPages);
    };

    paginateText();

    // Re-paginate on resize
    const handleResize = () => paginateText();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [story, fontSize]);

  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextPage();
    } else if (isRightSwipe) {
      handlePrevPage();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleNextPage = () => {
    if (currentPage < pages.length - 1 && !isTransitioning) {
      setIsTransitioning(true);
      setPreviousPage(currentPage);
      setCurrentPage((prev) => prev + 1);
      setTimeout(() => {
        setIsTransitioning(false);
        setPreviousPage(null);
      }, 300);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setPreviousPage(currentPage);
      setCurrentPage((prev) => prev - 1);
      setTimeout(() => {
        setIsTransitioning(false);
        setPreviousPage(null);
      }, 300);
    }
  };

  // Tap a word → open the dictionary popover. Doing this via per-word
  // spans (see renderPageWithTappableWords below) instead of the OS
  // text-selection API — on iOS the selection UI (blue handles + magnifier)
  // makes reading feel like editing, and long-press just to look up a
  // word is a chore.
  const handleWordTap = (word: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setSelectedWord(word);
    setPopoverPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
    setPopoverOpened(true);
  };

  // Close popover when clicking outside
  useEffect(() => {
    if (!popoverOpened) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      const popoverElement = document.querySelector('[data-dictionary-popover]');

      if (popoverElement && !popoverElement.contains(target)) {
        setPopoverOpened(false);
        window.getSelection()?.removeAllRanges();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [popoverOpened]);

  const handleClosePopover = () => {
    setPopoverOpened(false);
    window.getSelection()?.removeAllRanges();
  };

  const handleStartFluency = (studentId: string) => {
    if (!story) return;
    startFluencySession.mutate(
      { student_id: studentId, story_id: story.id },
      {
        onSuccess: (session) => {
          navigate(`/parent/fluency/session/${session.id}`);
        },
        onError: (error: unknown) => {
          const message = error instanceof Error ? error.message : 'Failed to start Read Aloud session';
          notifications.show({ message, color: 'red' });
        },
      },
    );
  };

  const handleBack = () => {
    if (assignmentId) {
      navigate('/assignments');
    } else {
      navigate(isParent ? '/library' : '/');
    }
  };

  const progress = pages.length > 0 ? ((currentPage + 1) / pages.length) * 100 : 0;

  // Inject CSS animations for page transitions
  useEffect(() => {
    const styleId = 'page-transition-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(-100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader color="teal" size="lg" />
      </Center>
    );
  }

  if (!story) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text c="dimmed">Story not found</Text>
          <Button variant="light" onClick={handleBack}>
            Go Back
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgb(240, 238, 230)',
      }}
    >
      {/* Top Bar */}
      <Box
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'rgba(240, 238, 230, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          paddingTop: isIOS ? 'env(safe-area-inset-top)' : 0,
        }}
      >
        <Group justify="space-between" p="md" gap="xs">
          <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleBack}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} lineClamp={1}>
              {story.title}
            </Text>
            <Text size="xs" c="dimmed">
              Page {currentPage + 1} of {pages.length}
            </Text>
          </Box>
          <Group gap="xs">
            {/* Font Size Controls - 3 Levels */}
            <Group gap={2} style={{
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              borderRadius: '8px',
              padding: '2px'
            }}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setFontSize('small')}
                style={{
                  backgroundColor: fontSize === 'small' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
                  fontWeight: fontSize === 'small' ? 600 : 400
                }}
              >
                <Text size="xs">A</Text>
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setFontSize('medium')}
                style={{
                  backgroundColor: fontSize === 'medium' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
                  fontWeight: fontSize === 'medium' ? 600 : 400
                }}
              >
                <Text size="sm">A</Text>
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setFontSize('large')}
                style={{
                  backgroundColor: fontSize === 'large' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
                  fontWeight: fontSize === 'large' ? 600 : 400
                }}
              >
                <Text size="md">A</Text>
              </ActionIcon>
            </Group>
            {isParent && fluencyChildren.length > 0 && (
              fluencyChildren.length === 1 ? (
                <ActionIcon
                  variant="light"
                  color="grape"
                  size="lg"
                  loading={startFluencySession.isPending}
                  onClick={() => handleStartFluency(fluencyChildren[0].id)}
                  aria-label="Read Aloud"
                >
                  <IconMicrophone size={20} />
                </ActionIcon>
              ) : (
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="light"
                      color="grape"
                      size="lg"
                      loading={startFluencySession.isPending}
                      aria-label="Read Aloud"
                    >
                      <IconMicrophone size={20} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Choose a child</Menu.Label>
                    {fluencyChildren.map((child) => (
                      <Menu.Item
                        key={child.id}
                        leftSection={<IconMicrophone size={16} />}
                        onClick={() => handleStartFluency(child.id)}
                      >
                        {child.display_name}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              )
            )}
            {popoverOpened && (
              <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleClosePopover}>
                <IconX size={20} />
              </ActionIcon>
            )}
          </Group>
        </Group>
        {/* Reading Progress Bar */}
        <Progress value={progress} size="xs" color="teal" />
      </Box>

      {/* Page Content - Swipeable */}
      <Box
        ref={pageContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {pages.length > 0 ? (
          <>
            {/* Outgoing Page (fades out during transition) */}
            {isTransitioning && previousPage !== null && (
              <Box
                style={{
                  animation: 'fadeOut 0.3s ease-out',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  position: 'absolute',
                  top: '24px',
                  left: '24px',
                  right: '24px',
                }}
              >
                <Text
                  style={{
                    fontFamily: "'Comic Neue', 'Comic Sans MS', cursive",
                    fontSize: fontSizes[fontSize],
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    cursor: 'default',
                    color: '#1a1a1a',
                    textAlign: 'left',
                  }}
                >
                  {renderPageWithTappableWords(pages[previousPage], handleWordTap)}
                </Text>
              </Box>
            )}

            {/* Current Page Text with Fade In Animation */}
            <Box
              key={currentPage}
              style={{
                animation: 'fadeIn 0.3s ease-out',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                position: isTransitioning ? 'absolute' : 'relative',
                top: isTransitioning ? '24px' : 'auto',
                left: isTransitioning ? '24px' : 'auto',
                right: isTransitioning ? '24px' : 'auto',
              }}
            >
              <Text
                style={{
                  fontFamily: "'Comic Neue', 'Comic Sans MS', cursive",
                  fontSize: fontSizes[fontSize],
                  lineHeight: '1.8',
                  whiteSpace: 'pre-wrap',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  cursor: 'default',
                  color: '#1a1a1a',
                  textAlign: 'left',
                }}
              >
                {renderPageWithTappableWords(pages[currentPage], handleWordTap)}
              </Text>
            </Box>

            {/* Navigation Arrows (Desktop fallback) */}
            {currentPage > 0 && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="xl"
                onClick={handlePrevPage}
                disabled={isTransitioning}
                style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                }}
              >
                <IconChevronLeft size={32} />
              </ActionIcon>
            )}
            {currentPage < pages.length - 1 && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="xl"
                onClick={handleNextPage}
                disabled={isTransitioning}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                }}
              >
                <IconChevronRight size={32} />
              </ActionIcon>
            )}
          </>
        ) : (
          <Center>
            <Loader color="teal" />
          </Center>
        )}
      </Box>

      {/* Dictionary Popover */}
      <DictionaryPopover
        word={selectedWord}
        opened={popoverOpened}
        position={popoverPosition}
        onClose={handleClosePopover}
        storyId={id}
        creditToStudentId={readingWithStudentId}
      />

      {/* Bottom Action Bar (Students only, shown on last page) */}
      {isStudent && currentPage === pages.length - 1 && (
        <Box
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            paddingBottom: isIOS ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
          }}
        >
          <Box p="md">
            <Button
              fullWidth
              size="lg"
              variant="filled"
              color="teal"
              onClick={() => navigate(`/quiz/${story.id}`, { state: { sessionId, readingTime } })}
              disabled={!sessionId}
              loading={startSession.isPending}
            >
              Continue to Quiz
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
